import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';

import { getAuthState } from '../../../../../store/AuthSlice';
import { useAppSelector } from '../../../../../hooks';
import { checkUserPermission } from '../../../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/V2/RolesSlice';

interface CreateCourseOptionsModalProps {
  open: boolean;
  onClose: () => void;
  viewTemplates: () => void;
}

const OptionCard = ({
  title,
  onClick,
}: {
  title: string;
  onClick: () => void;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        color: '#1E293B',
        border: '1px solid #CBD5E1',
        backgroundColor: '#F8FAFC',
        transition: 'all ease 0.3s',
        '&:hover': { backgroundColor: '#CBD5E1' },
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardContent style={{ padding: '14px 18px' }}>
          <Typography fontSize="14px" fontWeight={600} textAlign="center">
            {title}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const CreateCourseOptionsModal: React.FC<CreateCourseOptionsModalProps> = ({
  open,
  onClose,
  viewTemplates,
}) => {
  const navigate = useNavigate();

  // Store
  const authState = useAppSelector(getAuthState);

  const authUser = authState.user;
  const options = [
    {
      title: 'Create From Scratch',
      onClick: () => {
        navigate(`${process.env.PUBLIC_URL}/portal/courses/new`);
        onClose();
      },
    },
    {
      title: 'Create One to One From Scratch',
      onClick: () => {
        navigate(`${process.env.PUBLIC_URL}/portal/courses/new?type=1-1`);
        onClose();
      },
    },
  ];

  if (
    checkUserPermission(authUser, {
      adminCourseTemplate: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
      ],
    })
  ) {
    options.push({
      title: 'Create From a Template',
      onClick: () => {
        viewTemplates();
        onClose();
      },
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { minWidth: '600px', maxWidth: '600px' } }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
        Select an option
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {options.map((option, index) => (
            <Grid item key={`opt-${index + 1}`} xs={4}>
              <OptionCard title={option.title} onClick={option.onClick} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{
            color: '#000000',
            fontWeight: 600,
            fontSize: '12px',
            backgroundColor: '#E2E8F0',
          }}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCourseOptionsModal;
